import { Banner, Button, Modal } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

/**
 * The design's SaveBar (`rd-savebar`, not exported by the kit as a component):
 * it rises under a section once that section has unsaved edits, with Discard
 * and Save. Save submits the section's form by id.
 */
export function ProfileSaveBar({
  formId,
  visible,
  saving,
  onDiscard,
}: {
  formId: string;
  visible: boolean;
  saving: boolean;
  onDiscard: () => void;
}) {
  const t = useTranslations();
  if (!visible) return null;
  return (
    <div className="rd-savebar" role="region" aria-label={t(keys.forms.discardChangesTitle)} style={{ marginTop: 16 }}>
      <span>{t(keys.forms.discardChangesTitle)}</span>
      <button type="button" className="rd-savebar-discard" onClick={onDiscard} disabled={saving}>
        {t(keys.common.cancel)}
      </button>
      <button
        type="submit"
        form={formId}
        className="rd-savebar-save"
        disabled={saving}
        aria-busy={saving || undefined}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        {saving ? <span className="rd-spin" aria-hidden="true" /> : null}
        {saving ? t("admin.saving") : t(keys.common.save)}
      </button>
    </div>
  );
}

/** "Discard your changes?" for a section, fed by `useFormSafety`. */
export function ProfileDiscardModal({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useTranslations();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      width={440}
      title={t(keys.forms.discardChangesTitle)}
      actions={
        <>
          <Button variant="ghost" muted onClick={onCancel}>
            {t(keys.forms.discardChangesContinue)}
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            {t(keys.forms.discardChangesDiscard)}
          </Button>
        </>
      }
    >
      <p className="rd-modal-text" style={{ margin: 0 }}>
        {t(keys.forms.discardChangesBody)}
      </p>
    </Modal>
  );
}

/**
 * A failed save, at the top of the section that failed: what failed, the
 * server's reason, that nothing changed, and Try again (resubmits the form,
 * which still holds the edits).
 */
export function ProfileSaveError({
  title,
  message,
  formId,
  retrying,
}: {
  title: string;
  message: string;
  formId: string;
  retrying: boolean;
}) {
  const t = useTranslations();
  return (
    <Banner
      tone="error"
      title={title}
      actions={
        <Button type="submit" form={formId} size="sm" loading={retrying} loadingLabel={t(keys.shell.retrying)}>
          {t(keys.shell.retry)}
        </Button>
      }
    >
      {`${message} ${t("admin.nothingChanged")}`}
    </Banner>
  );
}
